import {
  type GetShiftsResponse,
  type ShiftType,
  usePaginatedWorkerShifts,
  type UsePaginatedWorkerShiftsOptions,
} from "@src/appV2/redesign/WorkerShifts/api/usePaginatedWorkerShifts";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { type AxiosError } from "axios";
import { useMemo } from "react";

import { type SupportShift } from "../types";

interface UseSupportShiftsParams {
  workerId: string;
  showCancelledShifts: boolean;
}

interface UseSupportShiftsResult {
  pastShifts: SupportShift[];
  upcomingShifts: SupportShift[];
  isLoading: boolean;
  isError: boolean;
  error: AxiosError | null;
  refetch: () => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
}

interface WorkplaceAttributes {
  name?: string;
  timezone?: string;
}

/** Type guard to check if attributes have workplace structure. */
function hasWorkplaceAttributes(attributes: unknown): attributes is WorkplaceAttributes {
  if (!attributes || typeof attributes !== "object") {
    return false;
  }
  const record = attributes as Record<string, unknown>;
  return (
    (record.name === undefined || typeof record.name === "string") &&
    (record.timezone === undefined || typeof record.timezone === "string")
  );
}

/** Finds workplace data from the included array by ID. */
function findWorkplace(
  workplaceId: string | undefined,
  included: GetShiftsResponse["included"]
): WorkplaceAttributes | undefined {
  if (!workplaceId || !included) {
    return undefined;
  }

  const workplace = included.find((item) => item.type === "workplace" && item.id === workplaceId);

  if (workplace?.type !== "workplace") {
    return undefined;
  }

  // Use type guard instead of unsafe assertion
  if (hasWorkplaceAttributes(workplace.attributes)) {
    return workplace.attributes;
  }

  return undefined;
}

/** Resolves workplace name from shift relationships and included data. */
function resolveWorkplaceName(shift: ShiftType, included: GetShiftsResponse["included"]): string {
  const workplaceId = shift.relationships?.workplace?.data?.id;
  const workplace = findWorkplace(workplaceId, included);
  return workplace?.name ?? "Unknown Workplace";
}

/** Resolves workplace timezone from shift relationships and included data. */
function resolveWorkplaceTimezone(
  shift: ShiftType,
  included: GetShiftsResponse["included"]
): string {
  const workplaceId = shift.relationships?.workplace?.data?.id;
  const workplace = findWorkplace(workplaceId, included);
  return workplace?.timezone ?? DEFAULT_TIMEZONE;
}

/**
 * Determines the completion status of a shift for display in the support flow.
 * Status is purely time-based: past or upcoming (future).
 * Cancellation is an orthogonal concern signaled by deleted === true.
 */
function determineShiftStatus(isPast: boolean): SupportShift["status"] {
  return isPast ? "past" : "upcoming";
}

/** Converts dollars to cents with rounding to handle floating-point precision issues. */
function dollarsToCents(dollars: number | undefined): number | undefined {
  if (dollars === undefined) {
    return undefined;
  }
  return Math.round(dollars * 100);
}

/**
 * Transforms API shift data to SupportShift format.
 * Returns raw numeric values; formatting happens in the UI layer.
 */
function transformToSupportShift(
  shift: ShiftType,
  included: GetShiftsResponse["included"],
  isPast: boolean
): SupportShift {
  const { attributes } = shift;

  return {
    id: shift.id,
    workplaceName: resolveWorkplaceName(shift, included),
    startAt: attributes?.schedule?.startAt ?? "",
    endAt: attributes?.schedule?.endAt ?? "",
    timezone: resolveWorkplaceTimezone(shift, included),
    status: determineShiftStatus(isPast),
    deleted: attributes?.deleted === true,
    // Raw hours as decimal - formatting happens in UI
    hoursWorkedInHours: attributes?.schedule?.durationWithoutBreaksInHours,
    // Convert dollars to cents once at the data layer to avoid float math issues
    paymentAmountInCents: dollarsToCents(attributes?.pay?.total),
    // Payment status is intentionally not set here.
    // Accurate payment status requires the payments API and is fetched
    // separately in the shift details view using useGetShiftPaymentStatus.
    cancellationDetails: attributes?.cancellationDetails ?? undefined,
  };
}

/**
 * Hook to fetch shifts for the guided support flow.
 *
 * Fetches past shifts (within 30 days) and upcoming shifts,
 * transforming them into the SupportShift format for display.
 *
 * @param params - Contains the workerId to fetch shifts for
 * @param options - React Query options
 * @returns Combined result with past and upcoming shifts
 */
export function useSupportShifts(
  params: UseSupportShiftsParams,
  options: UsePaginatedWorkerShiftsOptions = {}
): UseSupportShiftsResult {
  const { workerId, showCancelledShifts } = params;

  // startAt < now → Past tab, startAt > now → Upcoming tab. Simple rule.
  // Both active and cancelled shifts follow the same time-based categorization.
  const shiftFields = [
    "schedule.startAt",
    "schedule.endAt",
    "schedule.durationWithoutBreaksInHours",
    "pay.total",
    ...(showCancelledShifts ? ["deleted", "cancellationDetails"] : []),
  ];

  const filter = showCancelledShifts ? undefined : { deleted: false };

  // Use start <= now so ongoing shifts (started but not ended) appear in Past tab
  const pastShiftsQuery = usePaginatedWorkerShifts(
    {
      workerId,
      timeline: "PAST_AND_IN_PROGRESS",
      sortBy: "schedule.startAt",
      sortOrder: "desc",
      pageSize: 20,
      fields: {
        shift: shiftFields,
        workplace: ["name", "timezone"],
      },
      include: ["workplace"],
      filter,
    },
    options
  );

  const upcomingShiftsQuery = usePaginatedWorkerShifts(
    {
      workerId,
      timeline: "FUTURE",
      sortBy: "schedule.startAt",
      sortOrder: "asc",
      pageSize: 20,
      fields: {
        shift: shiftFields,
        workplace: ["name", "timezone"],
      },
      include: ["workplace"],
      filter,
    },
    options
  );

  const pastShifts = useMemo(() => {
    if (!pastShiftsQuery.data?.pages) {
      return [];
    }

    return pastShiftsQuery.data.pages.flatMap((page) =>
      page.data.map((shift) => transformToSupportShift(shift, page.included, true))
    );
  }, [pastShiftsQuery.data?.pages]);

  const upcomingShifts = useMemo(() => {
    if (!upcomingShiftsQuery.data?.pages) {
      return [];
    }

    return upcomingShiftsQuery.data.pages.flatMap((page) =>
      page.data.map((shift) => transformToSupportShift(shift, page.included, false))
    );
  }, [upcomingShiftsQuery.data?.pages]);

  const refetch = (): void => {
    void pastShiftsQuery.refetch();
    void upcomingShiftsQuery.refetch();
  };

  return {
    pastShifts,
    upcomingShifts,
    isLoading: pastShiftsQuery.isLoading || upcomingShiftsQuery.isLoading,
    isError: pastShiftsQuery.isError || upcomingShiftsQuery.isError,
    error: pastShiftsQuery.error ?? upcomingShiftsQuery.error,
    refetch,
    hasNextPage:
      (pastShiftsQuery.hasNextPage ?? false) || (upcomingShiftsQuery.hasNextPage ?? false),
    fetchNextPage: () => {
      if (pastShiftsQuery.hasNextPage) {
        void pastShiftsQuery.fetchNextPage();
      }

      if (upcomingShiftsQuery.hasNextPage) {
        void upcomingShiftsQuery.fetchNextPage();
      }
    },
    isFetchingNextPage:
      pastShiftsQuery.isFetchingNextPage || upcomingShiftsQuery.isFetchingNextPage,
  };
}
